import { z } from 'zod';

/**
 * What a stored file is. Each kind has its own folder in the bucket (`PROJECT_FILE_FOLDERS`). A later version of a
 * script is another `script` row: there is no version kind.
 */
export const PROJECT_FILE_KINDS = ['script', 'report', 'attachment'] as const;
export const projectFileKindSchema = z.enum(PROJECT_FILE_KINDS);
export type ProjectFileKind = z.infer<typeof projectFileKindSchema>;

/** The bucket folder of each kind. Mirrors `project_file_folder` in SQL. */
export const PROJECT_FILE_FOLDERS: Record<ProjectFileKind, string> = {
  script: 'scripts',
  report: 'reports',
  attachment: 'attachments',
};

/** The bucket every project file lives in. */
export const PROJECT_FILES_BUCKET = 'project-files';

/** A script is a PDF of at most 20 MB. `register_script_file` and the bucket enforce the same limit. */
export const SCRIPT_MAX_BYTES = 20 * 1024 * 1024;
export const SCRIPT_CONTENT_TYPE = 'application/pdf';

/**
 * Where a script is stored: `<project>/scripts/<file id>.pdf`. The customer's filename is kept in the file row
 * and never forms a path.
 */
export function scriptObjectName(projectId: string, fileId: string): string {
  return `${projectId}/${PROJECT_FILE_FOLDERS.script}/${fileId}.pdf`;
}

/** A row of `public.project_files`. Read by whoever may read the project; written only by the script RPCs. */
export const projectFileSchema = z.object({
  id: z.uuid(),
  project_id: z.uuid(),
  kind: projectFileKindSchema,
  object_name: z.string().min(1),
  original_filename: z.string().min(1).max(255),
  size_bytes: z.number().int().positive(),
  content_type: z.string().min(1),
  uploaded_by: z.uuid().nullable(),
  created_at: z.iso.datetime({ offset: true }),
});
export type ProjectFile = z.infer<typeof projectFileSchema>;
